import React from "react";
import { Link, useLocation } from "react-router-dom";

import fsciModel from "src/assets/images/fsci_report_logo.png";
import { V1Domains } from "src/pages/reports/fsci/v1/types";
import { V2Domains } from "src/pages/reports/fsci/v2/types";

import "./fsci_domains_circle.scss";

interface FSCIDomainsCircleProps {
    readonly inline?: boolean;
    readonly activeDomain?: V1Domains | V2Domains;
    readonly legacyLearningDomain?: boolean;
}

// The five links follow the outer ring in the original 1263 × 1293 artwork.
const pointOnRing = (radius: number, angle: number): string => {
    const radians = (angle * Math.PI) / 180;
    return `${635 + radius * Math.cos(radians)},${684 + radius * Math.sin(radians)}`;
};

const segmentPath = (start: number): string => {
    const end = start + 72;
    return `M${pointOnRing(582, start)} A582,582 0 0 1 ${pointOnRing(582, end)}
        L${pointOnRing(389, end)} A389,389 0 0 0 ${pointOnRing(389, start)} Z`;
};

const domains = [
    { domain: "purpose", section: "purpose", label: "Purpose", path: segmentPath(-162) },
    { domain: "relationships", section: "relationships", label: "Relationships", path: segmentPath(-90) },
    {
        domain: "teachingAndLearning",
        section: "teaching-and-learning",
        label: "Teaching and Learning",
        path: segmentPath(-18),
    },
    {
        domain: "expertiseAndResources",
        section: "expertise-and-resources",
        label: "Expertise and Resources",
        path: segmentPath(54),
    },
    { domain: "wellBeing", section: "well-being", label: "Well-Being", path: segmentPath(126) },
];

export const FSCIDomainsCircle = ({
    inline = false,
    activeDomain,
    legacyLearningDomain = false,
}: FSCIDomainsCircleProps): React.ReactElement => {
    const { pathname } = useLocation();
    const basePath = pathname.substring(0, pathname.lastIndexOf("/") + 1);

    return (
        <div className={`fsci-domains-circle${inline ? " fsci-domains-circle--inline" : ""}`}>
            <svg aria-label="Explore FSCI domains" role="group" viewBox="0 0 1263 1293">
                <image aria-hidden="true" height="1293" href={fsciModel} width="1263" />
                {domains.map(({ domain, section, label, path }) => {
                    const isLegacyLearning = legacyLearningDomain && domain === "teachingAndLearning";
                    const isActive = activeDomain === (isLegacyLearning ? "learningOrientation" : domain);
                    const linkLabel = isLegacyLearning ? "Learning Orientation" : label;

                    return (
                        <Link
                            aria-current={isActive ? "page" : undefined}
                            aria-label={linkLabel}
                            className={`fsci-domain-link${isActive ? " active" : ""}`}
                            key={domain}
                            to={`${basePath}${isLegacyLearning ? "learning-orientation" : section}`}
                        >
                            <title>{linkLabel}</title>
                            <path d={path} />
                        </Link>
                    );
                })}
            </svg>
        </div>
    );
};
